import { useEffect, useState, useCallback, useRef } from "react";
import { useToast } from "@cloudaccelerator-ui/react-library";
import { incentiveRequestService } from "@/services/incentiveRequestService";
import type { IApprovalRequest, ApprovalAction, ApprovalStatus } from "@/types/approvalTypes";

interface IUseApprovalsResult {
  approvals: IApprovalRequest[];
  isLoading: boolean;
  error: string | null;
  actionInProgress: string | null;
  refetch: () => void;
  handleAction: (approvalId: string, action: ApprovalAction) => Promise<void>;
}

export function useApprovals(): IUseApprovalsResult {
  const { notify } = useToast();
  const [approvals, setApprovals] = useState<IApprovalRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchCount, setFetchCount] = useState<number>(0);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const approvalsRef = useRef<IApprovalRequest[]>(approvals);
  approvalsRef.current = approvals;

  const refetch = useCallback((): void => {
    setFetchCount((prev) => prev + 1);
  }, []);

  const handleAction = useCallback(
    async (approvalId: string, action: ApprovalAction): Promise<void> => {
      const approval = approvalsRef.current.find((a) => a.id === approvalId);
      try {
        setActionInProgress(approvalId);
        await incentiveRequestService.submitApprovalAction(approvalId, action);
        const newStatus: ApprovalStatus = action === "Approve" ? "Approved" : "Rejected";
        setApprovals((prev) =>
          prev.map((a) =>
            a.id === approvalId ? { ...a, status: newStatus } : a,
          ),
        );

        const requestorName = approval?.requestor ?? "Unknown";
        const actionLabel = action === "Approve" ? "approved" : "rejected";
        notify({
          title: "Action Successful",
          body: `Request from ${requestorName} has been ${actionLabel} successfully.`,
          intent: "success",
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : `Failed to ${action.toLowerCase()} request`;
        setError(errorMessage);
        notify({
          title: "Action Failed",
          body: errorMessage,
          intent: "error",
        });
      } finally {
        setActionInProgress(null);
      }
    },
    [notify],
  );

  useEffect(() => {
    const abortController = new AbortController();

    const fetchApprovals = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        const data = await incentiveRequestService.getMyApprovals(abortController.signal);

        if (!abortController.signal.aborted) {
          setApprovals(data);
        }
      } catch (err) {
        if (abortController.signal.aborted) {
          return;
        }
        if (!abortController.signal.aborted) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to fetch approvals",
          );
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchApprovals();

    return (): void => {
      abortController.abort();
    };
  }, [fetchCount]);

  return { approvals, isLoading, error, actionInProgress, refetch, handleAction };
}
